import type { FC, ReactNode } from 'react';

import type { CmsAdapter, CmsPage, CmsSource } from '@eneco/cms-adapter';

import CmsLayout from './CmsLayout';
import CmsRouteContainer from './CmsRouteContainer';
import type { CmsRouteContainerProps } from './CmsRouteContainer';

export interface CmsLaneRouteProps extends Omit<CmsRouteContainerProps, 'adapter' | 'page' | 'children'> {
  /** The page the forced provider resolved server-side. Absent on every normal request. */
  cmsPage?: CmsPage | null;
  /** The provider `?_cms=` asked for, when the container lets the override through. */
  cmsForceProvider?: CmsSource | null;
  /** The container's provider selection. Returns nothing where the container's environment gate is closed. */
  selectCmsAdapter: (provider: CmsSource) => CmsAdapter | undefined;
  /** The container's own default lane, rendered whenever the CMS lane does not apply. */
  children: ReactNode;
}

/**
 * Picks the lane a container's catch-all page renders: the CMS-agnostic lane when `?_cms=` forced a
 * provider that resolved a page, and the container's own lane otherwise.
 *
 * The CMS lane renders the fetched page through the CMS-agnostic host and registry, whichever
 * provider produced it. Both providers share this one path and one registry on purpose: that is what
 * makes the two renders comparable. The container's own lane arrives as `children`, so this component
 * never needs to know how a container builds it (today, a `SitecoreRouteContainer` with that
 * container's props), and none of it mounts when the CMS lane is chosen.
 */
const CmsLaneRoute: FC<CmsLaneRouteProps> = ({ cmsPage, cmsForceProvider, selectCmsAdapter, children, ...host }) => {
  const adapter = cmsForceProvider ? selectCmsAdapter(cmsForceProvider) : undefined;

  if (!cmsPage || !adapter) return children;

  return (
    <CmsRouteContainer adapter={adapter} page={cmsPage} {...host}>
      <CmsLayout />
    </CmsRouteContainer>
  );
};

export default CmsLaneRoute;
